import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { connect } from 'react-redux';
import * as queries from './graphql/query';
import log from '../../utils/ClientLogger';
import LeadActions from '../../actions/lead';

export const UserCtx = createContext();

export const UserContext = (props) => {
    const {
        children
    } = props;
    const [epcUserId, setEpcUserId] = useState(null);
    const [paperContractUser, setPaperContractUser] = useState(null);
    const [epcUserInformation, setEpcUserInformation] = useState(null);

    const [loading, setLoading] = useState(false);
    const [hasBeenInitialized, setHasBeenInitialized] = useState(false);

    const {
        setEmail,
        setFirstName,
        setLastName,
        setLatinFirstName,
        setLatinLastName,
        setPhoneCountryCodeDigits,
        setPhoneNumber
    } = props;
    useEffect(() => {
        if (epcUserInformation) {
            setEmail(epcUserInformation.email);
            setFirstName(epcUserInformation.asciiFirstName ?? epcUserInformation.firstName);
            setLastName(epcUserInformation.asciiLastName ?? epcUserInformation.lastName);
            setLatinFirstName(epcUserInformation.firstName);
            setLatinLastName(epcUserInformation.lastName);
            setPhoneCountryCodeDigits(epcUserInformation.phoneCountryCode);
            setPhoneNumber(epcUserInformation.phoneNumber);
        }
    }, [epcUserInformation]);

    // epc user Id
    const epcOnError = (error) => {
        log(['error'], 'EPCUserInformation', error);
    };
    const epcOnComplete = (payload) => {
        const {
            errors: resultError,
            userId,
            userInformation
        } = payload.fetchEpcUserInformation;
        if (resultError) {
            log(['info'], 'EPCUserInformation', resultError);
        } else {
            setEpcUserId(userId);
            setEpcUserInformation(userInformation);
        }
        setHasBeenInitialized(true);
    };
    const [
        fetchEpcUserInformation,
        fetchEpcUserInformationCallState
    ] = useLazyQuery(queries.FETCH_EPC_USER_INFORMATION, {
        onCompleted: epcOnComplete,
        onError: epcOnError
    });

    const { setIsPaperContractUser } = props;
    const [
        checkForPaperContractUser,
        checkForPaperContractUserCallState
    ] = useLazyQuery(queries.IS_PAPER_CONTRACT_USER, {
        onCompleted: (response) => {
            setPaperContractUser(response.isEpcPaperContractUser); // sets it to context state
            setIsPaperContractUser(response.isEpcPaperContractUser); // sets it to redux state
        },
        onError: (error) => {
            log(['error'], 'IsPaperContractUser', error);
        }
    });

    useEffect(() => {
        setLoading(
            fetchEpcUserInformationCallState.loading
                || checkForPaperContractUserCallState.loading
        );
    }, [
        fetchEpcUserInformationCallState.loading,
        checkForPaperContractUserCallState.loading
    ]);

    // if the user is in page where login is not required, dont attempt to fetch user
    const getEpcUserId = async () => {
        if (epcUserId) {
            return epcUserId;
        }
        const response = await fetchEpcUserInformation();
        return response?.data?.fetchEpcUserInformation?.userId;
    };

    // if the user is in page where login is not required, dont attempt to fetch user
    const getEpcUserInformation = async () => {
        if (epcUserInformation) {
            return epcUserInformation;
        }
        const response = await fetchEpcUserInformation();
        return response?.data?.fetchEpcUserInformation?.userInformation;
    };

    const isPaperContractUser = async () => {
        if (paperContractUser) {
            return paperContractUser;
        }
        const result = await checkForPaperContractUser();
        return result?.data?.isEpcPaperContractUser;
    };

    const checkForPaperContractUserFn = async () => {
        await checkForPaperContractUser();
    };

    useEffect(() => {
        if (epcUserId) {
            checkForPaperContractUserFn();
        }
    }, [epcUserId]);

    return (
        <UserCtx.Provider value={{
            getEpcUserId,
            isPaperContractUser,
            getEpcUserInformation,
            paperContractUser,
            loading,
            epcUserId,
            hasBeenInitialized
        }}
        >
            {children}
        </UserCtx.Provider>
    );
};

UserContext.propTypes = {
    // redux hydration
    children: PropTypes.node.isRequired,
    // user information actions
    setEmail: PropTypes.func.isRequired,
    setFirstName: PropTypes.func.isRequired,
    setLastName: PropTypes.func.isRequired,
    setLatinFirstName: PropTypes.func.isRequired,
    setLatinLastName: PropTypes.func.isRequired,
    setPhoneCountryCodeDigits: PropTypes.func.isRequired,
    setPhoneNumber: PropTypes.func.isRequired,
    setIsPaperContractUser: PropTypes.func.isRequired
};

/* istanbul ignore next */
const mapStateToProps = () => ({});

/* istanbul ignore next */
// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = dispatch => ({
    setEmail: email => dispatch(LeadActions.setEmail(email)),
    setFirstName: firstName => dispatch(LeadActions.setFirstName(firstName)),
    setLastName: lastName => dispatch(LeadActions.setLastName(lastName)),
    setLatinFirstName: latinFirstName => dispatch(LeadActions.setLatinFirstName(latinFirstName)),
    setLatinLastName: latinLastName => dispatch(LeadActions.setLatinLastName(latinLastName)),
    setPhoneCountryCodeDigits: code => dispatch(LeadActions.setPhoneCountryCodeDigits(code)),
    setPhoneNumber: number => dispatch(LeadActions.setPhoneNumber(number)),
    setIsPaperContractUser: isPaperContractUser => dispatch(
        LeadActions.setIsPaperContractUser(isPaperContractUser)
    )
});

UserContext.displayName = 'UserContext';

export default connect(mapStateToProps, mapDispatchToProps)(UserContext);
